import React from "react"
import {Col, message, Popconfirm, Row, Tooltip} from "antd";
import IType, {
    COMPANY_WORKLOAD_TYPE_MODE_BREAK,
    COMPANY_WORKLOAD_TYPE_MODE_WORKING
} from "../../../../../model/interface/company/workload/IType";
import IObligation from "../../../../../model/interface/company/IObligation";
import EntriesService from "../../../../../model/service/company/workload/EntriesService";
import IClaim from "../../../../../model/interface/company/workload/IClaim";
import TypesService from "../../../../../model/service/company/workload/TypesService";
import {humanShiftHoursInDays} from "../../../file-manager/utils/TimeDuration";
import {IEntryState} from "../../../../../model/interface/company/workload/IEntry";
import ShiftsService from "../../../../../model/service/company/workload/ShiftsService";
import Button from "../../../../shared/button/Button";
import {DeleteOutlined, FilePdfOutlined, FileSyncOutlined, InfoCircleOutlined} from "@ant-design/icons";
import IReport from "../../../../../model/interface/company/workload/IReport";
import ReportsService from "../../../../../model/service/company/workload/ReportsService";
import moment from "moment";
import {ITimeSheetRow} from "../Month";
import IEmployee from "../../../../../model/interface/company/IEmployee";
import LocaleText from "../../../settings/dictionary/LocaleText";
import settingsService from "../../../../../model/service/SettingsService";
import SettingsService from "../../../../../model/service/SettingsService";
import {IAppState} from "../../../../../redux/store";
import selectors from "../../../../../redux/selectors";
import {connect} from "react-redux";
import IHoliday from "../../../../../model/interface/ui/IHoliday";

interface IProps {
    data: ITimeSheetRow[]
    obligation: IObligation,
    freeDays: IHoliday[]
    types: IType[]
    claims: IClaim[]
    report: IReport
    onChange: (report: IReport) => void
    getText: (code: string, fallBack?: string) => string
}

interface IState {
    filling?: boolean
    downloading?: boolean
    deleting?: boolean
}

class Summary extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {}
    }

    fillWithDefault() {
        const {report, onChange, getText} = this.props
        this.setState({filling: true})
        return ReportsService.getInstance().resourceUpdate(report.uuid, {}, {}, "fill_default")
            .then(resource => {
                message.success(getText('company.workload.report.summary.fill-with-default.success', 'Úspěšně doplněno')).then()
                this.setState({filling: false}, () => onChange(resource))
            }).catch(() => this.setState({filling: false}))
    }

    deleteNew() {
        const {report, onChange, getText} = this.props
        this.setState({deleting: true})
        return ReportsService.getInstance().resourceUpdate(report.uuid, {}, {}, "delete_all_new")
            .then(resource => {
                message.success(getText('company.workload.report.summary.delete-new.success', 'Úspěšně smazáno')).then()
                this.setState({deleting: false}, () => onChange(resource))
            })
    }


    download(report: IReport) {
        this.setState({downloading: true})
        return ReportsService.getInstance().download(report?.id).finally(() => this.setState({downloading: false}))
    }

    render() {
        const {filling, downloading, deleting} = this.state
        const {obligation, data, types, claims, report, freeDays} = this.props
        const workFond = report.workFond
        const workFondTillToday = Summary.getWorkFondTillToday(data, obligation, freeDays, report.employee)
        let workTotal = Summary.getWorkTotal(data)
        const overTimeLimit = SettingsService.getValue('company', 'report_month_over_time_limit')

        const allTillToday = Summary.getTillTodayTotal(data)
        workTotal = Math.abs(workTotal - Math.round(workTotal)) > 0.001 ? workTotal : Math.round(workTotal)
        const total = Summary.getTotal(data)
        const overTime = Math.max(total - workFondTillToday, 0)

        return (
            <div>
                {settingsService.getValue('company', 'report_month_summary_items')?.includes('fond') && (
                    <Row justify={"space-between"}>
                        <Tooltip placement={'leftTop'} title={<div>
                            <strong>
                                <LocaleText code={'company.workload.report.summary.month-fond'}
                                            fallback={'Pracovní fond tento měsíc'}/>
                            </strong>
                            <div>
                                <LocaleText code={'company.workload.report.summary.month-fond.tooltip'}
                                            fallback={'Celkový počet hodin, které je potřeba odpracovat v aktuálním kalendářním měsíci podle vaší pracovní smlouvy a úvazku.'}/>
                            </div>
                        </div>}>
                            <div>
                                <InfoCircleOutlined className={'mr-1'}/>
                                <LocaleText code={'company.workload.report.summary.month-fond'}
                                            fallback={'Pracovní fond tento měsíc'}/>:
                            </div>
                        </Tooltip>
                        <div>
                            {Summary.hoursToString(workFond)}
                            {this.showDaysInfo(obligation, workFond)}
                        </div>
                    </Row>
                )}
                {settingsService.getValue('company', 'report_month_summary_items')?.includes('fond_today') && (
                    <Row justify={"space-between"}>
                        <Tooltip placement={'leftTop'} title={<div>
                            <strong>
                                <LocaleText code={'company.workload.report.summary.today-fond'}
                                            fallback={'Pracovní fond k dnešnímu dni'}/>
                            </strong>
                            <div>
                                <LocaleText code={'company.workload.report.summary.today-fond.tooltip'}
                                            fallback={'Podílová část měsíčního fondu, která odpovídá počtu pracovních dnů uplynulých od začátku měsíce do dnešního dne. Slouží pro průběžnou kontrolu, zda plníte plán.'}/>
                            </div>
                        </div>}>
                            <div>
                                <InfoCircleOutlined className={'mr-1'}/>
                                <LocaleText code={'company.workload.report.summary.today-fond'}
                                            fallback={'Pracovní fond k dnešnímu dni'}/>:
                            </div>
                        </Tooltip>
                        <div>
                            {Summary.hoursToString(workFondTillToday)}
                            {this.showDaysInfo(obligation, workFondTillToday)}
                        </div>
                    </Row>
                )}
                {settingsService.getValue('company', 'report_month_summary_items')?.includes('work') && (
                    <Row justify={"space-between"}>
                        <Tooltip placement={'leftTop'} title={<div>
                            <strong>
                                <LocaleText code={'company.workload.report.summary.total-all'}
                                            fallback={'Vykázaná doba celkem'}/>
                            </strong>
                            <div>
                                <LocaleText code={'company.workload.report.summary.total-.tooltip'}
                                            fallback={'Souhrn veškerého času, který se vám započítává do odpracované doby. Zahrnuje nejen čas strávený na pracovišti, ale také práci z domova (Home Office), služební cesty a uznané absence (např. dovolenou nebo lékaře). Toto je klíčový údaj pro výpočet mzdy a plnění fondu.'}/>
                            </div>
                        </div>}>
                            <div>
                                <InfoCircleOutlined className={'mr-1'}/>
                                <LocaleText code={'company.workload.report.summary.total-all'}
                                            fallback={'Vykázaná doba celkem'}/>:
                            </div>
                        </Tooltip>
                        <div>
                            {Summary.hoursToString(total)}
                            {this.showDaysInfo(obligation, total)}
                        </div>
                    </Row>
                )}
                {settingsService.getValue('company', 'report_month_summary_items')?.includes('work_today') && (
                    <Row justify={"space-between"}>
                        <Tooltip placement={'leftTop'} title={<div>
                            <strong>
                                <LocaleText code={'company.workload.report.summary.total-work'}
                                            fallback={'Čas na pracovišti'}/>
                            </strong>
                            <div>
                                <LocaleText code={'company.workload.report.summary.total-work.tooltip'}
                                            fallback={'Údaj vycházející čistě z vaší fyzické docházky v budově firmy (pípnutí čipem při příchodu a odchodu). Nezahrnuje práci na dálku ani uznané absence.'}/>
                            </div>
                        </div>}>
                            <div>
                                <InfoCircleOutlined className={'mr-1'}/>
                                <LocaleText code={'company.workload.report.summary.total-work'}
                                            fallback={'Čas na pracovišti'}/>:
                            </div>
                        </Tooltip>

                        <div>
                            {Summary.hoursToString(workTotal)}
                            {this.showDaysInfo(obligation, workTotal)}
                        </div>
                    </Row>
                )}
                {settingsService.getValue('company', 'report_month_summary_items')?.includes('over_time') && (
                    <Row justify={"space-between"}>
                        <Tooltip placement={'leftTop'} title={<div>
                            <strong>
                                <LocaleText code={'company.workload.report.summary.over-time'}
                                            fallback={'Přesčas'}/>
                            </strong>
                            <div>
                                <LocaleText code={'company.workload.report.summary.over-time.tooltip'}
                                            fallback={'Hodiny odpracované nad rámec vaší směny v tomto měsíci.'}/>
                                {overTimeLimit && (
                                    <LocaleText code={'company.workload.report.summary.over-time-limit.tooltip'}
                                                fallback={'Limit za rok je stanovený na #hours h'}
                                                params={{'#hours': overTimeLimit}}/>
                                )}
                            </div>
                        </div>}>
                            <div>
                                <InfoCircleOutlined className={'mr-1'}/>
                                <LocaleText code={'company.workload.report.summary.over-time'}
                                            fallback={'Přesčas'}/>:
                            </div>
                        </Tooltip>
                        <Row align={'bottom'} className={'flex-column'}>
                            <div>
                                {Summary.hoursToString(overTime)} {overTime ? this.showDaysInfo(obligation, overTime) : ''}
                            </div>
                            {overTimeLimit ? (
                                <Tooltip placement={'leftTop'} title={<LocaleText
                                    code={'company.workload.report.summary.over-time-limit-left'}
                                    fallback={'Zůstatek'}/>}>
                                    <div className={'text-muted pl-2 d-inline'}>
                                        {overTimeLimit ? (
                                            (Summary.hoursToString(overTimeLimit - (report.yearOverTime || 0))) + ' z ' + Summary.hoursToString(overTimeLimit)
                                        ) : ''}
                                    </div>
                                </Tooltip>
                            ) : ''}
                        </Row>
                    </Row>
                )}

                {this.getPlanSummary(types, claims, data, obligation)}

                <Row justify={"space-between"}>
                    <div><LocaleText code={'company.workload.report.summary.today-missing'}
                                     fallback={'K dnešnímu dni chybí'}/>:
                    </div>
                    <div>
                        {Summary.hoursToString(Math.max(workFondTillToday - allTillToday, 0))}
                        {this.showDaysInfo(obligation, Math.max(workFondTillToday - allTillToday, 0))}
                    </div>
                </Row>
                <Row justify={'start'} gutter={[6, 6]} className={'mt-3'}>
                    <Col>
                        <Button icon={<FileSyncOutlined/>} type={'special'}
                                disabled={!report || !report._permissions?.['edit']}
                                onClick={() => this.fillWithDefault()} loading={filling}>
                            <LocaleText code={'company.workload.report.auto-fill'} fallback={'Doplnit dle úvazku'}/>
                        </Button>
                    </Col>
                    <Col>
                        <Button icon={<FilePdfOutlined className={'text-danger'}/>} loading={downloading}
                                disabled={!report}
                                onClick={() => report && this.download(report)}>
                            <LocaleText code={'company.workload.report.month.download'} fallback={'Stáhnout výkaz'}/>
                        </Button>
                    </Col>
                    {['new', 'returned'].includes(report.state) && (
                        <Col>
                            <Popconfirm title={'Opravdu chcete smazat všechny záznamy?'}
                                        onConfirm={() => report && this.deleteNew()}
                                        okText={<LocaleText code={'general.yes'} fallback={'Ano'}/>}
                                        cancelText={<LocaleText code={'general.no'} fallback={'Ne'}/>}>
                                <Button danger={true} icon={<DeleteOutlined/>}
                                        loading={deleting}
                                        disabled={!report}>
                                    <span>
                                        <LocaleText code={'company.workload.report.month.delete-all'}
                                                    fallback={'Smazat záznamy'}/>
                                    </span>
                                </Button>
                            </Popconfirm>
                        </Col>
                    )}
                </Row>
            </div>
        )
    }

    showDaysInfo(obligation: IObligation, workFond: number) {
        return obligation.dayWorkHours && workFond ? (
            <div className={'text-muted pl-2 d-inline'}>
                {humanShiftHoursInDays(workFond, obligation.dayWorkHours, this.props.getText)}
            </div>
        ) : '';
    }

    getPlanSummary(types: IType[], claims: IClaim[], data: ITimeSheetRow[], obligation: IObligation) {
        return TypesService.listPlanType(types)
            .map((type: IType) => {
                const index = claims.findIndex(c => c.type.uuid === type.uuid)
                const claim = index < 0 ? null : claims[index]
                const free = claim ? Math.round(10 * claim.amountFree) / 10 : 0;
                const available = claim ? Math.round(10 * claim.amountTotal) / 10 : 0
                const total = Summary.getTypeTotal(type, data, 'approved')

                return claim ? (
                    <div>
                        <Row justify={"space-between"}>
                            <div><LocaleText code={claim.type.title}/>{':'}</div>
                            <div>
                                {Summary.hoursToString(total)}
                                {this.showDaysInfo(obligation, total)}
                            </div>
                        </Row>
                        <Row justify={"space-between"} className={'text-muted'}>
                            <LocaleText code={'company.workload.report.summary.claims'} fallback={'Zůstatky'}/>
                            <div>
                                {obligation.dayWorkHours ? humanShiftHoursInDays(free, obligation.dayWorkHours, this.props.getText) : Summary.hoursToString(free)}
                                {' '}<LocaleText code={'general.off-total'} fallback={'z'}/>{' '}
                                {obligation.dayWorkHours ? humanShiftHoursInDays(available, obligation.dayWorkHours, this.props.getText) : Summary.hoursToString(available)}
                            </div>
                        </Row>
                    </div>
                ) : ''
            });
    }

    static getWorkTotal(data: ITimeSheetRow[]) {
        return data.reduce((daySum, a) => daySum + Summary.getWorkDayTotal(a), 0)
    }

    static getWorkDayTotal(a: ITimeSheetRow) {
        return a.entries.filter(e => e.type.mode === COMPANY_WORKLOAD_TYPE_MODE_WORKING)
            .reduce((entrySum, e) => entrySum + EntriesService.getInstance().countHours(e), 0)
    }

    static getDayTotal(a: ITimeSheetRow) {
        return a.entries.filter(e => e.type.mode !== COMPANY_WORKLOAD_TYPE_MODE_BREAK).reduce((entrySum, e) => entrySum + EntriesService.getInstance().countHours(e), 0)
    }

    static getWorkTillToday(data: ITimeSheetRow[]) {
        return data
            .reduce((entrySum, e) => entrySum + (moment().isSameOrAfter(e.date) ? Summary.getWorkDayTotal(e) : 0), 0)
    }

    static getTillTodayTotal(data: ITimeSheetRow[]) {
        return data.reduce((entrySum, e) => entrySum + (moment().isSameOrAfter(e.date) ? Summary.getDayTotal(e) : 0), 0)
    }

    static getTotal(data: ITimeSheetRow[]) {
        return data.reduce((entrySum, e) => entrySum + Summary.getDayTotal(e), 0)
    }

    static getWorkFondTillToday(data: ITimeSheetRow[], obligation: IObligation, freeDays: IHoliday[], employee?: IEmployee) {
        return data.reduce((daySum, a) => daySum + (a.date.isSameOrBefore(moment(), 'date') ?
            Summary.getWorkDayFond(obligation, a, freeDays, employee) : 0), 0)
    }

    static getWorkDayFond(obligation: IObligation, a: ITimeSheetRow, freeDays: IHoliday[], employee?: IEmployee) {
        return obligation?.shifts.filter(s => ShiftsService.isShiftActive(s, a.date, employee, freeDays))
            .reduce((shiftSum, s) => s.totalHours + shiftSum, 0)
    }

    static getTypeTotal(type: IType, data: ITimeSheetRow[], state?: IEntryState) {
        return data.reduce((daySum, a) => daySum + Summary.getTypeDayTotal(type, a, state), 0)
    }

    static getTypeDayTotal(type: IType, a: ITimeSheetRow, state?: IEntryState) {
        return a.entries.filter(e => e.type.uuid === type.uuid && (!state || state === e.state))
            .reduce((entrySum, e) => entrySum + EntriesService.getInstance().countHours(e), 0);
    }

    getOvertime(data: ITimeSheetRow[], obligation: IObligation, freeDays: IHoliday[]) {
        return data.reduce((daySum, a) =>
            daySum + Math.max(Summary.getWorkDayTotal(a) - Summary.getWorkDayFond(obligation, a, freeDays), 0), 0)
    }

    static hoursToString(number: number) {
        const hours = Math.floor(number)
        const minutes = '' + Math.round((number - hours) * 60)
        return hours.toString().padStart(2, "0") + ':' + minutes.padStart(2, "0")
    }
}

const mapStateToProps = (state: IAppState) => {
    return {
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)
    }
}

export default connect(mapStateToProps)(Summary)
